/**
 * Cómo completar una factura de compra que el SII informó en el RCV pero cuyo
 * XML no llegó (#1578). Antes el detalle solo decía «Las líneas se cargarán
 * cuando llegue el XML» y no ofrecía ninguna salida: en ditdental la usuaria no
 * encontró cómo registrar el gasto, y la Gem tampoco.
 *
 * Tres caminos: cargar el XML de esta factura, aprobarla con los montos del RCV
 * (si la empresa lo permite) o pedirle el XML al proveedor.
 */
import { useRef, useState } from 'react'
import { CheckCircle2, FileUp, Mail } from 'lucide-react'
import { Button } from '@/components/ds/Button'
import { TextField } from '@/components/ds/TextField'
import { getApiErrorMessage } from '@/lib/apiClient'
import {
  useRequestSupplierXml,
  useUploadXMLInvoice,
  type SupplierInvoice,
} from '@/modules/purchases/hooks/usePurchases'

export function CompletarFacturaPendiente({ factura, puedeCargar, puedeAprobar, aprobarSinXml, onAprobar, aprobando }: {
  factura: SupplierInvoice
  /** Cargar y pedir el XML: el backend exige el permiso de crear facturas de compra. */
  puedeCargar: boolean
  puedeAprobar: boolean
  /** El interruptor de la empresa «Permitir aprobar facturas de compra sin XML». */
  aprobarSinXml: boolean
  onAprobar?: () => void
  aprobando?: boolean
}) {
  const archivo = useRef<HTMLInputElement>(null)
  const cargar = useUploadXMLInvoice()
  const pedir = useRequestSupplierXml(factura.id)
  const [correo, setCorreo] = useState('')
  const [aviso, setAviso] = useState<{ tono: 'ok' | 'error'; texto: string } | null>(null)

  const alElegir = (e: React.ChangeEvent<HTMLInputElement>) => {
    const elegido = e.target.files?.[0]
    e.target.value = ''
    if (!elegido) return
    setAviso(null)
    cargar.mutate(elegido, {
      onSuccess: resultado => setAviso(resultado.id === factura.id
        ? { tono: 'ok', texto: 'XML cargado: la factura quedó completa y contabilizada.' }
        // La carga empareja por RUT, tipo y folio: un XML de otra factura la completa a ella.
        : { tono: 'error', texto: `Ese XML es de otra factura (N° ${resultado.invoice_number} de ${resultado.supplier_name}), y quedó cargada en ella. Esta sigue pendiente.` }),
      onError: e => setAviso({ tono: 'error', texto: getApiErrorMessage(e) }),
    })
  }

  return (
    <section aria-labelledby="completar-factura" className="p-4 rounded-lg border border-info-border bg-info-surface space-y-3">
      <div>
        <h3 id="completar-factura" className="text-sm font-semibold text-info-content">Cómo completar esta factura</h3>
        <p className="text-xs text-info-content-soft mt-0.5">
          El SII la informó en el Registro de Compras (RCV), pero su XML no llegó: por eso no tiene líneas ni está contabilizada.
        </p>
      </div>

      <ol className="space-y-3 text-sm text-content">
        <li className="space-y-1">
          <p className="font-medium">1. Cargar el XML de la factura</p>
          <p className="text-xs text-content-muted">
            Se descarga del portal del proveedor o llega al correo de intercambio. Clobi completa las líneas y la contabiliza.
          </p>
          {puedeCargar ? (
            <>
              <input ref={archivo} type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={alElegir}
                aria-label="Archivo XML de la factura" />
              <Button size="sm" icon={<FileUp size={14} />} loading={cargar.isPending} onClick={() => archivo.current?.click()}>
                Cargar XML
              </Button>
            </>
          ) : (
            <p className="text-xs text-content-subtle">Hace falta el permiso para crear facturas de compra.</p>
          )}
        </li>

        <li className="space-y-1">
          <p className="font-medium">2. Aprobarla con los montos del SII, sin XML</p>
          {aprobarSinXml ? (
            puedeAprobar && onAprobar ? (
              <>
                <p className="text-xs text-content-muted">
                  Se contabiliza con el neto, el IVA y el total del RCV, en una sola cuenta de gasto que se elige al aprobar.
                  Si después llega el XML, se asocia sin contabilizar de nuevo.
                </p>
                <Button size="sm" variant="secondary" icon={<CheckCircle2 size={14} />} loading={aprobando} onClick={onAprobar}>
                  Aprobar
                </Button>
              </>
            ) : (
              <p className="text-xs text-content-subtle">Hace falta el permiso para aprobar facturas de compra.</p>
            )
          ) : (
            <p className="text-xs text-content-muted">
              Está desactivado para la empresa. Un administrador lo activa en <span className="font-medium">Configuración → Localización</span>, con
              la opción «Permitir aprobar facturas de compra sin XML».
            </p>
          )}
        </li>

        <li className="space-y-1">
          <p className="font-medium">3. Pedirle el XML al proveedor</p>
          <p className="text-xs text-content-muted">Se le envía un correo para que lo reenvíe al correo de intercambio de la empresa.</p>
          {puedeCargar && (
          <div className="flex items-end gap-2">
            <TextField dense label="Correo del proveedor (opcional)" value={correo} onChange={e => setCorreo(e.target.value)}
              placeholder="El de la ficha del proveedor" inputProps={{ type: 'email' }} className="flex-1 min-w-0" />
            <Button size="sm" variant="ghost" icon={<Mail size={14} />} loading={pedir.isPending}
              onClick={() => pedir.mutate(correo.trim(), {
                onSuccess: r => setAviso({ tono: 'ok', texto: `Pedido enviado a ${r.sent_to}.` }),
                onError: e => setAviso({ tono: 'error', texto: getApiErrorMessage(e) }),
              })}>
              Pedir XML
            </Button>
          </div>
          )}
        </li>
      </ol>

      {aviso && (
        <p role={aviso.tono === 'error' ? 'alert' : 'status'}
          className={`text-xs ${aviso.tono === 'error' ? 'text-danger-content-soft' : 'text-success-content'}`}>
          {aviso.texto}
        </p>
      )}
    </section>
  )
}
